import React from 'react';
import { T } from '../theme';
import { Card, Eyebrow, H1, Stage, Sub } from '../primitives';
import type { NextStepVM } from '../../formatters/deckContent';

interface Props {
  steps: NextStepVM[];
}

/** Act 6 — Next Steps: Now/Future timeline with a "Later" phase divider. */
export function ActNextSteps({ steps }: Props) {
  const nowCount = steps.filter((s) => s.when === 'Now').length;
  const futureCount = steps.length - nowCount;
  const firstFuture = steps.findIndex((s) => s.when === 'Future');

  const sub =
    nowCount > 0 && futureCount > 0
      ? `${nowCount === 1 ? 'One step' : `${nowCount} steps`} to start now, ${
          futureCount === 1 ? 'one that unfolds' : `${futureCount} that unfold`
        } over time.`
      : 'The steps that put your plan into motion.';

  return (
    <Stage>
      <Eyebrow>What's next</Eyebrow>
      <H1>Your implementation plan</H1>
      <Sub>{sub}</Sub>
      {steps.length === 0 ? (
        <Card>
          <div style={{ fontSize: 14.5, color: T.ink, lineHeight: 1.6 }}>
            Your advisor will walk through implementation with you.
          </div>
        </Card>
      ) : (
        <div style={{ position: 'relative', paddingLeft: 8 }}>
          <div style={{ position: 'absolute', left: 70, top: 12, bottom: 12, width: 2, background: T.grayLine }} />
          {steps.map((s, i) => {
            const future = s.when === 'Future';
            return (
              <React.Fragment key={i}>
                {/* phase divider before the first future step */}
                {i === firstFuture && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '6px 0 22px', paddingLeft: 76 }}>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 1.2,
                        textTransform: 'uppercase',
                        color: T.gray,
                      }}
                    >
                      Later
                    </span>
                    <span style={{ flex: 1, height: 1, background: T.grayLine }} />
                  </div>
                )}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 20,
                    marginBottom: 22,
                    position: 'relative',
                    opacity: future ? 0.72 : 1,
                  }}
                >
                  <div
                    style={{
                      width: 56,
                      textAlign: 'right',
                      fontSize: 12.5,
                      color: future ? T.gray : T.teal,
                      fontWeight: 600,
                      paddingTop: 7,
                    }}
                  >
                    {s.when}
                  </div>
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 15,
                      flexShrink: 0,
                      zIndex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: 14,
                      background: future ? T.card : T.teal,
                      color: future ? T.gray : '#fff',
                      border: future ? `2px solid ${T.grayLine}` : 'none',
                    }}
                  >
                    {i + 1}
                  </div>
                  <Card
                    style={{
                      flex: 1,
                      padding: '14px 18px',
                      background: future ? '#F4F6F8' : T.card,
                      boxShadow: future ? 'none' : '0 2px 10px rgba(27,42,74,.07)',
                      border: future ? `1px solid ${T.grayLine}` : 'none',
                    }}
                  >
                    <div style={{ fontWeight: 700, color: future ? '#5E6B7C' : T.navy, fontSize: 15.5, marginBottom: 3 }}>
                      {s.title}
                    </div>
                    <div style={{ fontSize: 13.5, color: T.gray, lineHeight: 1.5 }}>{s.body}</div>
                  </Card>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </Stage>
  );
}
